import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { useGetUserPermissions, UserPermissionsDTO } from '@/api/generated';
import { useUser } from '@/hooks/entities/useUser';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseUserPermissionsResult = {
  value?: UserPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<UserPermissionsDTO, unknown>> | undefined;
};

export const useUserPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseUserPermissionsResult => {
  const user = useUser();

  const userId = variables?.userId ?? user.userId;

  const {
    data: userPermissions,
    error,
    isLoading,
    refetch
  } = useGetUserPermissions(userId!, {
    query: { enabled: !!userId }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = !!userId && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? userPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
